import React, {useCallback, useState} from 'react';
import cn from 'classnames';
import {t} from '@lingui/core/macro';

import {userReportAPI} from 'features/userReports/api/userReportAPI';
import {ExceededModal, IUserOutput, useActiveToggle} from 'entities/user';
import {useNotifyChannelNames} from 'entities/notification';
import EntityActionsMenu from 'shared/ui/MoreActions/EntityActionsMenu';
import Envelope from 'shared/ui/icons/Envelope';
import TelegramNotBackground from 'shared/ui/icons/TelegramNotBackground';
import MaxChat from 'shared/ui/icons/MaxChat';
import YesNo from 'shared/ui/YesNo/YesNo';
import StatusToggle from 'shared/ui/StatusToggle/StatusToggle';
import IUserReport from 'features/userReports/model/UserReport/IUserReport';
import {getUserReportPeriodLabel} from 'features/userReports/lib/userReportFilters';
import {getFormattedDate} from 'shared/lib/datetime';
import {isTranslate} from 'shared/lib/lang';

import {PALETTE} from 'shared/config/model/palette';

import UserReportModal from '../UserReportModal/UserReportModal';
import UserReportDeleteModal from '../UserReportDeleteModal/UserReportDeleteModal';

import styles from './UserReportRow.module.scss';

interface IUserReportRowProps {
    item: IUserReport,
    user: IUserOutput | null,
    canCreate: boolean,
}

type ModalType = 'edit' | 'delete' | 'limit' | null;

const UserReportRow: React.FC<IUserReportRowProps> = ({item, user, canCreate}) => {
    const [activeModal, setActiveModal] = useState<ModalType>(null);
    const [updateUserReport] = userReportAPI.useUpdateUserReportMutation();

    const handleCloseModal = () => setActiveModal(null);

    const channelNames = useNotifyChannelNames();
    const additionalEmails = channelNames.additionalEmails(item.additional_emails);
    const telegramChats = channelNames.telegramChats(item.telegram_chats);
    const maxChats = channelNames.maxChats(item.max_chats);

    const hasAnyChannel = item.notify_by_email
        || additionalEmails.length > 0
        || telegramChats.length > 0
        || maxChats.length > 0;

    const {isActive, toggle} = useActiveToggle({
        initial: item.is_active,
        update: useCallback(
            (next: boolean) => updateUserReport({uuid: item.uuid, is_active: next}),
            [updateUserReport, item.uuid],
        ),
        activatedText: t`Отчёт активирован`,
        deactivatedText: t`Отчёт деактивирован`,
        canActivate: canCreate,
        onLimitExceeded: useCallback(() => setActiveModal('limit'), []),
    });

    return (
        <>
            <tr className={cn(styles.row, !isActive && styles.inactive)}>
                <td className={styles.nameCell}>
                    <span className={styles.name}>{item.name}</span>
                </td>
                <td>{getUserReportPeriodLabel(item.period)}</td>
                <td className={styles.monitorsCell}>{item.monitors_count}</td>
                <td>
                    {hasAnyChannel ? (
                        <div className={styles.channels}>
                            {item.notify_by_email && (
                                <div className={styles.channelLine}>
                                    <Envelope color={PALETTE.accent}/>
                                    <span className={styles.channelValue}>{user?.email}</span>
                                </div>
                            )}
                            {additionalEmails.map(({id, name}) => (
                                <div key={id} className={styles.channelLine}>
                                    <Envelope color={PALETTE.accent}/>
                                    <span className={styles.channelValue}>{name}</span>
                                </div>
                            ))}
                            {telegramChats.map(({id, name}) => (
                                <div key={id} className={styles.channelLine}>
                                    <TelegramNotBackground/>
                                    <span className={styles.channelValue}>{name}</span>
                                </div>
                            ))}
                            {maxChats.map(({id, name}) => (
                                <div key={id} className={styles.channelLine}>
                                    <MaxChat color="colored"/>
                                    <span className={styles.channelValue}>{name}</span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <span className={styles.empty}>—</span>
                    )}
                </td>
                <td>
                    <YesNo
                        condition={item.hide_banner && !!user?.tariff.report_hide_banner_enabled}
                        color={PALETTE.success}
                    />
                </td>
                <td className={styles.dateCell}>
                    {getFormattedDate(item.created_at, {
                        format: !isTranslate() ? 'DD.MM.YYYY' : 'YYYY-MM-DD',
                        tzString: user?.timezone,
                    })}
                </td>
                <td className={styles.statusCell}>
                    <StatusToggle
                        condition={isActive}
                        onClick={toggle}
                        preventToggle
                        textActive={t`Активен`}
                        notActiveText={t`Неактивен`}
                    />
                </td>
                <td onClick={(e) => e.stopPropagation()}>
                    <div className="d-flex justify-content-center">
                        <EntityActionsMenu
                            placement="auto"
                            onEdit={() => setActiveModal('edit')}
                            onDelete={() => setActiveModal('delete')}
                        />
                    </div>
                </td>
            </tr>

            <UserReportModal
                item={item}
                open={activeModal === 'edit'}
                handleClose={handleCloseModal}
            />

            <UserReportDeleteModal
                item={item}
                open={activeModal === 'delete'}
                handleClose={handleCloseModal}
            />

            <ExceededModal
                isOpen={activeModal === 'limit'}
                handleClose={handleCloseModal}
                type="reports"
            />
        </>
    );
};

export default UserReportRow;
